import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useAuth } from '../contexts/AuthContext'
import { NetworkBackdrop } from '../components/ui'
import { usePageTitle } from '../hooks/usePageTitle'
import { validateEmail } from '../utils/emailValidation'
import { safeLoginNextPath } from '../utils/loginRedirect'

interface LocationState {
  from?: { pathname: string }
}

/**
 * Spartan, editorial login page. No logos, no decorative panels —
 * the form is the only thing on the screen.
 */
const LoginPage: React.FC = () => {
  usePageTitle('Log ind')
  const { login, user } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const fromState = (location.state as LocationState | null)?.from?.pathname
  const from = safeLoginNextPath(fromState ?? searchParams.get('next'))

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailError, setEmailError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (user) {
    return <Navigate to={from} replace />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const check = validateEmail(email)
    if (!check.ok) {
      setEmailError(check.message)
      return
    }
    setEmailError(null)
    setSubmitting(true)
    try {
      await login(check.email, password)
      navigate(from, { replace: true })
    } catch (err: any) {
      setError(err?.message || 'Login fejlede')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-[10px] border border-rule bg-paper p-7 sm:p-8">
        <header className="relative mb-8">
          {/* Sits to the right of the small title block. */}
          <NetworkBackdrop className="absolute right-0 top-0 w-36 h-20" />
          <h1 className="relative text-[26px] leading-[1.15] text-ink">Log ind</h1>
          <p className="mt-2 text-sm text-ink-500">
            Værktøjet er kun for godkendte brugere.
          </p>
        </header>

        <form onSubmit={handleSubmit} noValidate className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-[13px] font-semibold text-ink-500 mb-1">
              E-mail
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) {
                  const r = validateEmail(e.target.value)
                  setEmailError(r.ok ? null : r.message)
                }
              }}
              onBlur={() => {
                const r = validateEmail(email)
                setEmailError(r.ok ? null : r.message)
              }}
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'email-error' : undefined}
              className={`w-full border-b-2 bg-transparent px-0 py-2 text-base text-ink placeholder:text-ink-300 focus:outline-none ${
                emailError
                  ? 'border-signal focus:border-signal'
                  : 'border-rule focus:border-ink'
              }`}
            />
            {emailError && (
              <p id="email-error" role="alert" className="mt-1 text-xs text-signal">
                {emailError}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="password" className="block text-[13px] font-semibold text-ink-500 mb-1">
              Adgangskode
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border-b-2 border-rule focus:border-ink bg-transparent px-0 py-2 text-base text-ink focus:outline-none"
            />
          </div>

          {error && (
            <div
              role="alert"
              className="text-sm text-signal-700 bg-signal-50 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-ink hover:bg-ink-700 disabled:bg-ink-300 text-paper text-sm font-semibold rounded-md py-3 transition-colors"
          >
            {submitting ? 'Logger ind…' : 'Log ind'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default LoginPage
